import React, { useState } from 'react';
import emailjs from '@emailjs/browser';

interface Props {
  onOpenPrivacyModal?: () => void;
}

const ContactUs: React.FC<Props> = ({ onOpenPrivacyModal }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
    privacyAccepted: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  // Validation feedback states
  const [highlightName, setHighlightName] = useState(false);
  const [highlightEmail, setHighlightEmail] = useState(false);
  const [highlightMessage, setHighlightMessage] = useState(false);
  const [nameError, setNameError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [messageError, setMessageError] = useState('');

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));

    // Clear global error message when user starts typing
    if (errorMessage) {
      setErrorMessage('');
    }

    // Clear field-specific errors when user starts fixing them
    if (name === 'name' && value.trim() && nameError) {
      setNameError('');
    }
    if (name === 'email') {
      if (value.trim() && validateEmail(value) && emailError) {
        setEmailError('');
      }
    }
    if (name === 'message' && value.trim() && messageError) {
      setMessageError('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Reset all errors first
    setNameError('');
    setEmailError('');
    setMessageError('');
    setErrorMessage('');

    let hasErrors = false;

    // Name validation with visual feedback
    if (!formData.name.trim()) {
      setHighlightName(true);
      setTimeout(() => setHighlightName(false), 1000);
      hasErrors = true;
    }

    // Email validation with visual feedback
    if (!formData.email.trim()) {
      setHighlightEmail(true);
      setTimeout(() => setHighlightEmail(false), 1000);
      hasErrors = true;
    } else if (!validateEmail(formData.email)) {
      setHighlightEmail(true);
      setTimeout(() => setHighlightEmail(false), 1000);
      hasErrors = true;
    }

    // Message validation with visual feedback
    if (!formData.message.trim()) {
      setHighlightMessage(true);
      setTimeout(() => setHighlightMessage(false), 1000);
      hasErrors = true;
    }

    // Don't proceed if validation fails
    if (hasErrors) {
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');

    // Check EmailJS credentials for contact form
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
    const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const contactTemplateId = process.env.REACT_APP_EMAILJS_CONTACT_TEMPLATE_ID;

    // WINNING COMBINATION: Test set 1 that succeeded!
    // Based on successful diagnostic test
    const templateParams = {
      name: formData.name.trim(),              // Maps to {{name}} in template
      email: formData.email.trim(),            // User's email address
      message: formData.message.trim()         // User's message content
    };

    try {
      if (!publicKey || !serviceId || !contactTemplateId) {
        throw new Error('EmailJS inte konfigurerat. Vänligen kontakta oss direkt via e-post.');
      }

      // Enhanced debug logging
      console.log('📧 EmailJS Send Attempt:', {
        publicKey: publicKey ? `${publicKey.slice(0, 5)}...` : 'MISSING',
        serviceId: serviceId ? `${serviceId.slice(0, 8)}...` : 'MISSING',
        contactTemplateId: contactTemplateId ? contactTemplateId : 'MISSING',
        templateParams: templateParams,
        timestamp: new Date().toISOString()
      });

      // Initialize EmailJS and send with winning parameters
      emailjs.init(publicKey);
      await emailjs.send(serviceId, contactTemplateId, templateParams);

      console.log('✅ Email sent successfully with winning parameters:', templateParams);

      setSubmitStatus('success');
      setFormData({ name: '', email: '', message: '', privacyAccepted: false });

      // Reset success status after 5 seconds
      setTimeout(() => setSubmitStatus('idle'), 5000);

    } catch (error) {
      console.error('🚨 Contact form error details:', {
        error: error,
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : 'No stack trace',
        templateParams: templateParams,
        credentials: {
          publicKey: publicKey ? 'SET' : 'MISSING',
          serviceId: serviceId ? 'SET' : 'MISSING',
          contactTemplateId: contactTemplateId ? 'SET' : 'MISSING'
        },
        timestamp: new Date().toISOString()
      });

      setSubmitStatus('error');

      // Enhanced error messaging based on error type
      let userMessage = 'Misslyckades att skicka meddelande. Prova e-postalternativet nedan.';

      if (error instanceof Error) {
        if (error.message.includes('422') || error.message.includes('template')) {
          userMessage = 'E-postmall kunde inte hittas. Kontakta oss direkt via e-post nedan.';
        } else if (error.message.includes('401') || error.message.includes('forbidden')) {
          userMessage = 'Autentiseringsfel. Kontakta oss direkt via e-post nedan.';
        } else if (error.message.includes('network') || error.message.includes('fetch')) {
          userMessage = 'Nätverksfel. Kontrollera din internetanslutning och försök igen.';
        } else {
          userMessage = error.message;
        }
      }

      setErrorMessage(userMessage);
      setTimeout(() => setSubmitStatus('idle'), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };


  return (
    <section className="bg-gradient-to-r from-primary-50 via-blue-50 to-indigo-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">
            Kontakta oss
          </h2>
        </div>

        {/* Success Toast Notification */}
        {submitStatus === 'success' && (
          <div className="fixed top-4 right-4 z-50 bg-green-600 text-white px-6 py-4 rounded-lg shadow-xl flex items-center space-x-3 animate-fade-in">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="font-semibold">Meddelande skickat!</span>
          </div>
        )}

        {/* Contact Form Container */}
        <div className="w-full">
          {/* Error Messages Only */}
          {(submitStatus === 'error' || errorMessage) && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg" role="alert">
              <p className="text-red-800 text-sm">{errorMessage || 'Något gick fel. Prova e-postalternativet nedan.'}</p>
            </div>
          )}

          <div className="bg-white rounded-lg shadow-md p-3 sm:p-4">
            <form onSubmit={handleSubmit} className="space-y-6" noValidate>
            {/* Name and Email Row - constrained to same width as message box */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="contact-name" className="sr-only">Ditt namn</label>
                  <input
                    type="text"
                    id="contact-name"
                    name="name"
                    placeholder="Ditt namn *"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                    className={`w-full px-4 py-3 sm:px-3 sm:py-2 text-base sm:text-sm border rounded-md text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-1 transition-all duration-300 disabled:opacity-50 disabled:bg-gray-100 ${
                      highlightName
                        ? 'border-red-500 shadow-red-200 shadow-lg animate-pulse focus:ring-red-500 focus:border-red-500'
                        : nameError
                        ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                        : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
                    }`}
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="sr-only">Din e-post</label>
                  <input
                    type="email"
                    id="contact-email"
                    name="email"
                    placeholder="Din e-post *"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                    className={`w-full px-4 py-3 sm:px-3 sm:py-2 text-base sm:text-sm border rounded-md text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-1 transition-all duration-300 disabled:opacity-50 disabled:bg-gray-100 ${
                      highlightEmail
                        ? 'border-red-500 shadow-red-200 shadow-lg animate-pulse focus:ring-red-500 focus:border-red-500'
                        : emailError
                        ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                        : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
                    }`}
                  />
                </div>
              </div>
              <div className="lg:col-span-1"></div>
            </div>

            {/* Message Box and Button Row */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              <div className="lg:col-span-3">
                <label htmlFor="contact-message" className="sr-only">Skriv ditt meddelande här</label>
                <textarea
                  id="contact-message"
                  name="message"
                  placeholder="Ditt meddelande *"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows={4}
                  disabled={isSubmitting}
                  className={`w-full px-4 py-3 sm:px-3 sm:py-2 text-base sm:text-sm border rounded-md text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-1 resize-none transition-all duration-300 disabled:opacity-50 disabled:bg-gray-100 ${
                    highlightMessage
                      ? 'border-red-500 shadow-red-200 shadow-lg animate-pulse focus:ring-red-500 focus:border-red-500'
                      : messageError
                      ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                      : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
                  }`}
                />
              </div>

              {/* Button Column */}
              <div className="lg:col-span-1 flex flex-col items-start">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full px-8 py-3 sm:px-6 sm:py-2 text-lg sm:text-base font-bold bg-primary-600 text-white hover:bg-primary-700 hover:shadow-xl rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-400 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg cursor-pointer whitespace-nowrap flex-shrink-0"
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-2 sm:mr-1 h-5 w-5 sm:h-4 sm:w-4 text-white" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span className="text-lg sm:text-base">Skickar...</span>
                    </div>
                  ) : (
                    <span className="text-lg sm:text-base">Skicka meddelande</span>
                  )}
                </button>

                {/* Privacy Policy Text - directly under button */}
                <div className="text-sm sm:text-xs text-gray-600 text-left mt-2 leading-relaxed">
                  Jag godkänner{' '}
                  <button
                    type="button"
                    onClick={onOpenPrivacyModal}
                    className="text-gray-600 hover:text-gray-800 cursor-pointer bg-transparent border-none p-0"
                  >
                    Integritetspolicyn
                  </button>
                </div>
              </div>
            </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactUs;